import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Divider, Select, Tooltip } from '@mantine/core';
import {
  ArrowsMaximize,
  ArrowsMove,
  Copy,
  FlipHorizontal,
  FlipVertical,
  FocusCentered,
  Maximize,
  Palette,
  ShirtSport,
  Trash
} from 'tabler-icons-react';
import AppStack from '../../common/AppStack';
import AppFlexbox from '../../common/AppFlexbox';
import AppCard from '../../common/AppCard';
import PrintDesignEditorCanvasLayersControls from './PrintDesignEditorCanvasLayersControls';
import {
  FONT_FAMILIES,
  CUSTOM_FONT_FAMILIES
} from '../../../config/fontConstants';
import AppMenuSelect from '../../common/AppMenuSelect';
import { ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM } from '../../../config/constants';
import CustomColorInput from '../../common/CustomColorInput';

const PrintDesignEditorCanvasControls = ({
  canvasOverlayRef,
  printTemplateState,
  canvasController,
  loading,
  toolbarMenuState,
  setToolbarMenuState
}) => {
  const {
    templateConfig,
    getCanvasSaveState,
    setItemPosition,
    setItemProperties,
    deleteObjectById,
    copyObjectById,
    positionObjectToTemplateById,
    fitObjectToTemplateById
  } = canvasController;
  const canvasSaveState = getCanvasSaveState();
  const [viewState, setViewState] = useState({
    showLayerControls: false,
    showSelectedLayerControls: false,
    showHeader: false
  });
  const canvasItems = canvasSaveState.items.filter(
    (i) => !i.hidden && i.visible
  );

  const selectedObjectId =
    printTemplateState.selectedObjects?.length === 1 &&
    printTemplateState.selectedObjects[0].id;
  const selectedObjectIndex = canvasItems.findIndex(
    (i) =>
      i.id === selectedObjectId ||
      (i.containerId && i.containerId === selectedObjectId)
  );
  const selectedObject = canvasItems[selectedObjectIndex];
  const objectPositioningId = selectedObject?.containerId || selectedObject?.id;

  useEffect(() => {
    if (printTemplateState.selectedObjects) {
      setViewState((prev) => ({
        ...prev,
        showHeader: printTemplateState.selectedObjects.length > 0,
        showLayerControls:
          prev.showLayerControls ||
          printTemplateState.selectedObjects.length > 0,
        showSelectedLayerControls:
          printTemplateState.selectedObjects.length === 1
      }));
    }
  }, [printTemplateState.selectedObjects]);

  return (
    <AppStack
      style={{
        flex: 1,
        width: '100%',
        height: '100%',
        position: 'absolute',
        overflow: 'hidden',
        pointerEvents: 'none',
        alignItems: 'end',
        gap: 5
      }}
    >
      <AppFlexbox
        style={{
          width: '100%',
          height: 60,
          pointerEvents: viewState.showHeader ? 'all' : 'none'
        }}
      >
        <AppCard
          radius={4}
          shadow="md"
          style={{
            width: '100%',
            height: '100%',
            padding: 0,
            display: viewState.showHeader && selectedObject ? 'flex' : 'none'
          }}
          withBorder
        >
          <AppFlexbox
            style={{ flex: 1, height: '100', alignItems: 'center', gap: 0 }}
          >
            <AppFlexbox style={{ padding: 8 }}>
              <Button
                color="dark"
                leftSection={<ArrowsMove size={18} />}
                onClick={() => {
                  setViewState((prev) => ({
                    ...prev,
                    showLayerControls: true,
                    showSelectedLayerControls: false
                  }));
                }}
                variant="outline"
              >
                Manage layers
              </Button>
            </AppFlexbox>
            <Divider orientation="vertical" />
            <AppFlexbox style={{ padding: 8, gap: 8 }}>
              {selectedObject?.fkEcomPrintDesignItemType ===
              ECOM_PRINT_DESIGN_ITEM_TYPE_ENUM.IMAGE ? (
                <Button.Group
                  style={{ border: 'solid 1px #dee2e6', borderRadius: 4 }}
                >
                  <Tooltip label="Flip horizontal" withArrow>
                    <Button
                      color="dark"
                      onClick={() => {
                        setItemPosition(objectPositioningId, {
                          ...selectedObject,
                          flipX: !selectedObject.flipX
                        });
                      }}
                      size="compact-md"
                      style={{ height: '100%' }}
                      variant="subtle"
                    >
                      <FlipVertical size={22} />
                    </Button>
                  </Tooltip>

                  <Divider orientation="vertical" />

                  <Tooltip label="Flip vertical" withArrow>
                    <Button
                      color="dark"
                      onClick={() => {
                        setItemPosition(objectPositioningId, {
                          ...selectedObject,
                          flipY: !selectedObject.flipY
                        });
                      }}
                      size="compact-md"
                      style={{ height: '100%' }}
                      variant="subtle"
                    >
                      <FlipHorizontal size={22} />
                    </Button>
                  </Tooltip>
                </Button.Group>
              ) : (
                <>
                  <AppMenuSelect
                    data={[
                      ...CUSTOM_FONT_FAMILIES.map((f) => ({
                        ...f,
                        section: 'Custom fonts'
                      })),
                      ...FONT_FAMILIES.map((f) => ({
                        ...f,
                        section: 'Standard fonts'
                      }))
                    ]}
                    onChange={(value) => {
                      setItemProperties(selectedObject.id, {
                        ...selectedObject,
                        fontFamily: value
                      });
                    }}
                    style={{ maxWidth: 160 }}
                    value={selectedObject?.fontFamily || null}
                  />

                  <Tooltip
                    label="Text size automatically adapts to the textbox area"
                    withArrow
                  >
                    <Select
                      data={[
                        {
                          label: 'Auto',
                          value: 'auto'
                        }
                      ]}
                      disabled
                      style={{ maxWidth: 80 }}
                      value="auto"
                    />
                  </Tooltip>

                  <Button.Group
                    style={{ border: 'solid 1px #dee2e6', borderRadius: 4 }}
                  >
                    <CustomColorInput
                      control={
                        <Tooltip label="Font color" withArrow>
                          <Button
                            color="dark"
                            size="compact-md"
                            style={{ height: '100%' }}
                            variant="subtle"
                          >
                            <Palette size={22} />
                          </Button>
                        </Tooltip>
                      }
                      onChange={(value) => {
                        if (selectedObject) {
                          setItemProperties(selectedObject.id, {
                            ...selectedObject,
                            itemProperties: {
                              ...selectedObject.itemProperties,
                              fontColor: value
                            }
                          });
                        }
                      }}
                      value={selectedObject?.itemProperties?.fontColor || null}
                    />
                  </Button.Group>
                </>
              )}

              <Button.Group
                style={{ border: 'solid 1px #dee2e6', borderRadius: 4 }}
              >
                <Tooltip label="Fit to template with padding" withArrow>
                  <Button
                    color="dark"
                    onClick={() => {
                      fitObjectToTemplateById(objectPositioningId, 'fit', true);
                    }}
                    size="compact-md"
                    style={{ height: '100%' }}
                    variant="subtle"
                  >
                    <FocusCentered size={22} />
                  </Button>
                </Tooltip>
                <Tooltip label="Fit to template" withArrow>
                  <Button
                    color="dark"
                    onClick={() => {
                      fitObjectToTemplateById(objectPositioningId, 'fit');
                    }}
                    size="compact-md"
                    style={{ height: '100%' }}
                    variant="subtle"
                  >
                    <Maximize size={22} />
                  </Button>
                </Tooltip>
                <Tooltip label="Fill template" withArrow>
                  <Button
                    color="dark"
                    onClick={() => {
                      fitObjectToTemplateById(objectPositioningId, 'fill');
                    }}
                    size="compact-md"
                    style={{ height: '100%' }}
                    variant="subtle"
                  >
                    <ArrowsMaximize size={22} />
                  </Button>
                </Tooltip>
              </Button.Group>

              <Button.Group
                style={{ border: 'solid 1px #dee2e6', borderRadius: 4 }}
              >
                <Tooltip label="Copy" withArrow>
                  <Button
                    color="dark"
                    onClick={() => {
                      copyObjectById(selectedObject.id);
                    }}
                    size="compact-md"
                    style={{ height: '100%' }}
                    variant="subtle"
                  >
                    <Copy size={22} />
                  </Button>
                </Tooltip>

                <Divider orientation="vertical" />

                <Tooltip label="Remove" withArrow>
                  <Button
                    color="dark"
                    onClick={() => {
                      deleteObjectById(selectedObject.id);
                    }}
                    size="compact-md"
                    style={{ height: '100%' }}
                    variant="subtle"
                  >
                    <Trash size={22} />
                  </Button>
                </Tooltip>
              </Button.Group>

              {templateConfig?.defaultPositioning && (
                <Button.Group
                  style={{ border: 'solid 1px #dee2e6', borderRadius: 4 }}
                >
                  <Tooltip label="Use recommended positioning" withArrow>
                    <Button
                      color="dark"
                      onClick={() => {
                        positionObjectToTemplateById(
                          objectPositioningId,
                          'default'
                        );
                      }}
                      size="compact-md"
                      style={{ height: '100%' }}
                      variant="subtle"
                    >
                      <ShirtSport size={22} />
                    </Button>
                  </Tooltip>
                </Button.Group>
              )}
            </AppFlexbox>
          </AppFlexbox>
        </AppCard>
      </AppFlexbox>

      <AppStack
        style={{
          right: 0,
          top: 60,
          width: 275,
          maxHeight: '100%',
          pointerEvents: 'all',
          display:
            viewState.showLayerControls && canvasItems.length > 0
              ? 'flex'
              : 'none'
        }}
      >
        <PrintDesignEditorCanvasLayersControls
          canvasController={canvasController}
          canvasItems={canvasItems}
          canvasOverlayRef={canvasOverlayRef}
          loading={loading}
          selectedObject={selectedObject}
          selectedObjectIndex={selectedObjectIndex}
          setViewState={setViewState}
          viewState={viewState}
        />
      </AppStack>
    </AppStack>
  );
};

PrintDesignEditorCanvasControls.propTypes = {
  canvasController: PropTypes.object.isRequired,
  canvasOverlayRef: PropTypes.object.isRequired,
  printTemplateState: PropTypes.object.isRequired,
  setToolbarMenuState: PropTypes.func.isRequired,
  toolbarMenuState: PropTypes.object.isRequired,
  loading: PropTypes.bool
};

export default PrintDesignEditorCanvasControls;
